import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import SharedDialog from '../../../../../shared/shared-dialog';
import { useState } from 'react';
import { Calendar, CalendarEventModel } from '../../../../../../types';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  UserKeys,
  getCalendarEvent,
  updateCalendarEvent,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { dayjsWithExtensions } from '../../../../../utils/dayjs-config';
import {
  formatEventStartPayload,
  formatEventEndPayload,
} from '../../../../../utils/calendar-events-utils';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../../shared/avatar-chip';
import { calendarColors } from '../../../../../utils/calendar-colors';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface CalendarEditEventDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  isAllUsers: boolean;
  selectedCalendar: Calendar;
  selectedEvent: CalendarEventModel;
}

export default function CalendarEditEventDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  isAllUsers,
  selectedCalendar,
  selectedEvent,
}: CalendarEditEventDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const [notifyUsers, setNotifyUsers] = useState(false);
  const [updateAllEvents, setUpdateAllEvents] = useState(false);

  const { data: calEventWithRecurringInfo } = useQuery({
    queryKey: [
      UserKeys.CalendarEvent,
      selectedCalendar.id,
      selectedEvent.recurringEventId,
    ],
    queryFn: () =>
      getCalendarEvent(selectedCalendar.id, selectedEvent.recurringEventId!),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 1,
    enabled: !!selectedEvent.recurringEventId,
  });

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: (data: Partial<CalendarEventModel>) => {
      const recurrenceData: Partial<CalendarEventModel> = {
        ...calEventWithRecurringInfo,
        id: selectedEvent.recurringEventId!,
        summary: data.summary || '',
        location: data.location,
        description: data.description,
        colorId: data.colorId === 'default' ? null : data.colorId,
        start: formatEventStartPayload(data.start),
        end: formatEventEndPayload(data.start, data.end),
        recurrence: calEventWithRecurringInfo?.recurrence,
        attendees: data.attendees,
        sequence: selectedEvent.sequence,
      };

      const updatedData = {
        ...data,
        colorId: data.colorId === 'default' ? null : data.colorId,
        start: formatEventStartPayload(data.start),
        end: formatEventEndPayload(data.start, data.end),
      };

      return updateCalendarEvent(
        userEmail,
        selectedCalendar.id,
        updateAllEvents ? selectedEvent.recurringEventId! : selectedEvent.id,
        notifyUsers,
        updateAllEvents ? recurrenceData : updatedData,
        // not moving to different calendar
        null,
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar event updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.CalendarEvents, selectedCalendar.id, userEmail],
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      setUpdateAllEvents(false);
      setNotifyUsers(false);
      setAutoCompleteValue('');
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.update_calendar_event
          : analytics.user_management.user_details.calendars
              .update_calendar_event,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { isLoading, isDirty },
  } = useForm<Partial<CalendarEventModel>>({
    defaultValues: {
      ...selectedEvent,
      description: selectedEvent.description || '',
      location: selectedEvent.location || '',
      colorId: selectedEvent.colorId || 'default',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'attendees',
  });

  const onSubmit: SubmitHandler<Partial<CalendarEventModel>> = (data) => {
    // Validate that end date is not before start date
    if (data.start?.dateTime && data.end?.dateTime) {
      const startDate = dayjsWithExtensions(data.start.dateTime);
      const endDate = dayjsWithExtensions(data.end.dateTime);
      if (endDate.isBefore(startDate)) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'End date must be on or after the start date.',
        });
        return;
      }
    }

    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.UpdateEvents.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const allDay = watch('start.allDay');
  const startDateTime = watch('start.dateTime');

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setChangeLogMessage('');
        setNotifyUsers(false);
        setUpdateAllEvents(false);
        setAutoCompleteValue('');
        reset();
      }}
      title="Update Event"
      dialogContent={
        <Stack gap={1.5} justifyContent="flex-start">
          <Stack flexDirection="row" gap={1}>
            <Controller
              name="summary"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  label="Title"
                  autoComplete="off"
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="colorId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'colorId',
                  }}
                  InputLabelProps={{
                    htmlFor: 'colorId',
                  }}
                  select
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                      },
                    },
                  }}
                  label="Color"
                  sx={{ width: 80 }}
                >
                  <MenuItem key={'default'} value={'default'}>
                    <Box
                      sx={{
                        backgroundColor:
                          calendarColors.calendar[
                            selectedCalendar.colorId as keyof typeof calendarColors.event
                          ].background,
                        width: 20,
                        height: 20,
                        mx: 'auto',
                      }}
                    ></Box>
                  </MenuItem>
                  {Object.keys(calendarColors.event).map((colorId) => (
                    <MenuItem key={colorId} value={colorId}>
                      <Box
                        sx={{
                          backgroundColor:
                            calendarColors.event[
                              colorId as keyof typeof calendarColors.event
                            ].background,
                          width: 20,
                          height: 20,
                          mx: 'auto',
                        }}
                      ></Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          <Box>
            <Stack flexDirection="row" justifyContent="space-between">
              <Controller
                name="start.dateTime"
                control={control}
                render={({ field: { value, onChange } }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={dayjsWithExtensions(value)}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue.valueOf());
                            setValue('end.dateTime', newValue.valueOf(), {
                              shouldDirty: true,
                            });
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  } else {
                    return (
                      <DateTimePicker
                        value={dayjsWithExtensions(value)}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue.valueOf());
                            setValue(
                              'end.dateTime',
                              newValue.add(30, 'minutes').valueOf(),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  }
                }}
              />
              <Controller
                name="end.dateTime"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || !startDateTime) return true;
                    const endDate = dayjsWithExtensions(value);
                    const startDate = dayjsWithExtensions(startDateTime);
                    return (
                      !endDate.isBefore(startDate) ||
                      'End date must be on or after the start date'
                    );
                  },
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={dayjsWithExtensions(value)}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue.valueOf());
                          }
                        }}
                        label="To"
                        sx={{ minWidth: 220 }}
                        minDate={
                          startDateTime != null
                            ? dayjsWithExtensions(startDateTime)
                            : undefined
                        }
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message || ' ',
                          },
                        }}
                      />
                    );
                  }
                  return (
                    <DateTimePicker
                      value={dayjsWithExtensions(value)}
                      onChange={(newValue) => {
                        if (newValue) {
                          onChange(newValue.valueOf());
                        }
                      }}
                      label="To"
                      sx={{ minWidth: 220 }}
                      minDateTime={
                        startDateTime != null
                          ? dayjsWithExtensions(startDateTime)
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message || ' ',
                        },
                      }}
                    />
                  );
                }}
              />
            </Stack>
            <Controller
              name="start.allDay"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      name="calendarEvents"
                    />
                  }
                  label="All Day"
                />
              )}
            />
          </Box>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ flexGrow: 1 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteValue}
                setAutoCompleteValue={setAutoCompleteValue}
                onSelection={(value) => {
                  if (!value) return;
                  append({
                    email: value,
                  });
                  setAutoCompleteValue('');
                }}
              />
            </Box>
          </Stack>
          <Stack
            flexDirection="row"
            sx={{
              maxHeight: 80,
              overflowY: 'auto',
              flexWrap: 'wrap',
            }}
            gap={0.5}
          >
            {fields.map((field, index) => {
              return (
                <AvatarChip
                  key={field.id}
                  email={field.email}
                  type={'User'}
                  onDelete={() => remove(index)}
                />
              );
            })}
          </Stack>

          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Location"
                autoComplete="off"
                sx={{ minWidth: 250 }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                autoComplete="off"
                sx={{ minWidth: 250 }}
                multiline
                rows={3}
              />
            )}
          />
          <Stack flexDirection="row" justifyContent="space-between">
            {/* {selectedEvent.recurringEventId ? (
              isLoadingRecurringEvent || isFetchingRecurringEvent ? (
                <Skeleton variant="rounded" width={261} height={42} />
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updateAllEvents}
                      onChange={(e) => setUpdateAllEvents(e.target.checked)}
                    />
                  }
                  label="Update all events in the series"
                />
              )
            ) : null} */}

            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyUsers}
                  onChange={(e) => setNotifyUsers(e.target.checked)}
                />
              }
              label="Notify guests of changes"
            />
          </Stack>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate(getValues());
            }}
            mutateLoading={isCreating}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit(onSubmit),
        loading: isCreating || isLoading,
        disabled: isCreating || isLoading || !isDirty,
      }}
    />
  );
}
